import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setRefreshProfiles } from '../../store/slices/preferencesSlice';
import { decreaseInvitations, addWeeklyInvitations } from '../../store/slices/authSlice';
import { setCachedProfiles, updateCachedRemovedProfiles, clearProfilesCache, updateSwipeStats } from '../../store/slices/cacheSlice';
import TinderCard from 'react-tinder-card';
import {getFirestore, doc, setDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import SettingsOffcanvas from '../Settings/SettingsOffcanvas';
import InviteOffcanvas from './InviteOffcanvas';
import InvitationsOffcanvas from '../Profile/InvitationsOffcanvas';
import MessageModal from './MessageModal';
import useLocationCheck from '../../hooks/useLocationCheck';
import MatchScreen from '../Match/MatchScreen';
import GpsBanner from '../Location/GpsBanner';
import { calculateAge, calculateDistance } from '../../utils/profileUtils';
import moment from 'moment';
import { Spin } from 'antd';
import ProfileDetail from '../ProfileDetail/ProfileDetail';
import { useTranslation } from 'react-i18next';
import './Home.css';
import SubscriptionOffcanvas from '../Subscription/SubscriptionOffcanvas';

import { hasActiveSubscription, hasPlatinumAccess } from '../../utils/subscriptionUtils';
import { cardService } from '../../services/cardService';

const FREE_DAILY_SWIPE_LIMIT = 25;

const Home = () => {
  const { isChecking, checkLocation } = useLocationCheck();
  const [profiles, setProfiles] = useState([]);
  const [removedProfiles, setRemovedProfiles] = useState([]); // Historique des profils supprimés
  const [, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [title, setTitle] = useState('');
  const { t } = useTranslation();
  // Get user data from Redux store
  const userId = useSelector(state => state.auth.uid);
  const { distance, refreshProfiles } = useSelector(state => state.preferences);
  const { ageSearch, genderSearch } = useSelector(state => state.auth);
  const authState = useSelector(state => state.auth);
  const { invitations, lastInviteRefresh } = authState;
  const { profilesCache, swipeStats } = useSelector(state => state.cache);

  const [, setDailySwipes] = useState(0);
  const [lastSwipeDate, setLastSwipeDate] = useState(null);
  const [viewProfile, setViewProfile] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [showMatchScreen, setShowMatchScreen] = useState(false);
  const [matchedProfile, setMatchedProfile] = useState(null);
  const [matchId, setMatchId] = useState(null);
  const [pendingCard, setPendingCard] = useState(null);
  const [isInviteLoading, setIsInviteLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Récupérer la localisation depuis locationSlice (nouveau système)
  const location = useSelector(state => state.location.location);
  const locationStrategy = useSelector(state => state.location.strategy);
  const showGpsBanner = useSelector(state => state.location.showGpsBanner);
  const longitude = location?.coordinates?.[0] || 0;
  const latitude = location?.coordinates?.[1] || 0;
  const hasValidLocation = location?.coordinates?.length === 2;
  const isTemporaryLocation = locationStrategy?.isTemporary || false;
  const dispatch = useDispatch();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [, setLastDirection] = useState();
  const currentIndexRef = useRef(currentIndex);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [currentImageIndexes, setCurrentImageIndexes] = useState({});
  const [showSwipeIndicators] = useState(true);
  const initialLoadRef = useRef(true);

  const [showInviteOffcanvas, setShowInviteOffcanvas] = useState(false);
  const [showInvitationsOffcanvas, setShowInvitationsOffcanvas] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('plus');

  const childRefs = useMemo(
    () =>
      Array(profiles.length)
        .fill(0)
        .map((i) => React.createRef()),
    [profiles.length]
  );

  useEffect(() => {
    if (profiles.length > 0) {
      setCurrentProfile(profiles[profiles.length - 1]);
    }
  }, [profiles]);

  useEffect(() => {
    // Reset daily swipes at midnight
    const now = new Date();
    const lastDate = lastSwipeDate ? new Date(lastSwipeDate) : null;
    if (!lastDate || lastDate.getDate() !== now.getDate()) {
      setDailySwipes(0);
      setLastSwipeDate(now.toISOString());
    }
  }, [lastSwipeDate]);

  useEffect(() => {
    // Check and update weekly invitations
    const now = new Date();
    const lastRefresh = lastInviteRefresh ? new Date(lastInviteRefresh) : null;
    
    if (!lastRefresh || now.getTime() - lastRefresh.getTime() >= 7 * 24 * 60 * 60 * 1000) {
      dispatch(addWeeklyInvitations());
    }
  }, [dispatch, lastInviteRefresh]);

  useEffect(() => {
    if (profilesCache.removedProfiles) {
      setRemovedProfiles(profilesCache.removedProfiles);
    }
  // Volontairement exécuté une seule fois au montage pour initialiser removedProfiles depuis le cache
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canGoBack =  hasActiveSubscription(authState);

  const canSwipe = () => {

    const today = new Date().toISOString().split('T')[0];
    // Si c'est un nouveau jour ou pas de stats encore
    if (swipeStats.lastSwipeDate !== today) {
      return true;
    }
    // Vérifier la limite selon le type d'abonnement
    const limit = !hasActiveSubscription(authState) ? FREE_DAILY_SWIPE_LIMIT : Infinity;
    return swipeStats.dailySwipes < limit;

  };

  const handleSwipe = async (dir) => {
    
    // Check subscription requirements
    if (dir === 'up' && invitations <= 0) {
      setShowInvitationsOffcanvas(true);
      return;
    }

    if ((dir === 'right' || dir === 'left') && !canSwipe()) {
      setShowSubscriptionModal(true);
      setCurrentPlan('plus');
      setTitle(t('home.subscription.dailyLimitTitle'));
      return;
    }

    if (currentIndex >= 0 && currentIndex < profiles.length) {
      await childRefs[currentIndex].current.swipe(dir);
    }
  };

  const outOfFrame = async(name, idx, dir) => {

    if (dir === 'up') {
      console.log('swiping up')
      return;
    }
    if ((dir === 'right' || dir === 'left') && !canSwipe()) {
      await childRefs[currentIndex].current.restoreCard();
      return;
    }
    
    // Si c'est le profil actuel qui est swipé
    if (idx === currentIndexRef.current) {
      
      // Préparer les nouvelles données
      const newProfiles = profiles.filter((_, index) => index !== idx);

      
      // Mettre à jour les états séparément
      setProfiles(newProfiles);
      updateProfilesCache(newProfiles);
      // Mettre à jour l'index pour pointer vers le dernier élément
      updateCurrentIndex(newProfiles.length - 1);
    }
  };

  const goBack = async () => {
    if (!canGoBack) return;
    
    try {
      const lastRemovedProfile = removedProfiles[0];
      const newRemovedProfiles = removedProfiles.slice(1);
      const newProfiles = [...profiles, lastRemovedProfile];
      
      // Construire le swipeId
      const swipeId = `${authState.uid}_${lastRemovedProfile.uid}`;
      
      
      // Envoyer la requête DELETE
      await cardService.deleteSwipe(swipeId);
      
      // Mettre à jour les états après la suppression réussie
      setProfiles(newProfiles);
      setRemovedProfiles(newRemovedProfiles);
      dispatch(updateCachedRemovedProfiles(newRemovedProfiles));
      updateCurrentIndex(newProfiles.length - 1);
      updateProfilesCache(newProfiles);
      console.log('Swipe deleted successfully');
    } catch (error) {
      console.error('Error deleting swipe:', error);
    }
  };

  const handleImageNavigation = (profileId, direction) => {
    
    // Préparer les nouvelles données
    const currentIndex = currentImageIndexes[profileId] ?? 0;
    const profile = profiles.find(p => p.uid === profileId);
    
    if (!profile) {
      return;
    }
    
    const maxIndex = profile.photos.length - 1;
    let newIndex;
    
    if (direction === 'next') {
      newIndex = currentIndex >= maxIndex ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex <= 0 ? maxIndex : currentIndex - 1;
    }
    
    // Mettre à jour l'état en une seule fois
    const newState = { ...currentImageIndexes, [profileId]: newIndex };
    setCurrentImageIndexes(newState);
  };

  const updateSwipingClass = (profileId, direction) => {
    const card = document.querySelector(`[data-profile-id="${profileId}"]`);
    if (card) {
      // Retirer toutes les classes de swipe
      card.classList.remove('swiping-right', 'swiping-left');
      
      // Ajouter la classe appropriée selon la direction
      if (direction === 'right') {
        card.classList.add('swiping-right');
      } else if (direction === 'left') {
        card.classList.add('swiping-left');
      }
    }
  };

  const fetchProfiles = async (isInitialFetch = false) => {
    if (isInitialFetch) {
      setIsInitialLoading(true);
    } else {
      setIsFetching(true);
    }
    
    try {

 

      const currentParams = { distance, genderPreference:genderSearch, ageSearch };
  

      // Vérifier si on peut utiliser le cache
      if (isCacheValid() && !checkParamsChanged(currentParams) && profilesCache.data.length > 0 && isInitialFetch) {
        setProfiles(profilesCache.data);
        setRemovedProfiles(profilesCache.removedProfiles || []);
        updateCurrentIndex(profilesCache.data.length - 1);
        setLoading(false);
        setIsFetching(false);
        return;
      }

      // Si le cache n'est pas valide ou les paramètres ont changé, faire une nouvelle requête
      const data = await cardService.getProfiles(
        userId,
        authState.ageSearch[0],
        authState.ageSearch[1],
        longitude,
        latitude,
        distance,
        genderSearch
      );
      setCanLoadMore(data.length >= 5);
      // Préparer les nouvelles données
      let newProfiles = refreshProfiles ? data : [...data, ...profiles];
      
      // Retirer les doublons en gardant les profils les plus bas dans la pile
      const seen = new Map();
      newProfiles = newProfiles.reverse().filter(profile => {
        if (seen.has(profile.uid)) {
          return false;
        }
        seen.set(profile.uid, true);
        return true;
      }).reverse();
      
      // Mettre à jour le cache avec les nouveaux profils et paramètres
      dispatch(setCachedProfiles({
        profiles: newProfiles,
        params: currentParams
      }));

      // Mettre à jour les profils et l'index
      setProfiles(newProfiles);
      updateCurrentIndex(newProfiles.length - 1);
      
      dispatch(setRefreshProfiles(false));
    } catch (err) {
      console.error('Error fetching profiles:', err);
      setError('Failed to load profiles');
    } finally {
      if (isInitialFetch) {
        setIsInitialLoading(false);
      } else {
        setIsFetching(false);
      }
      // setLoading(false);
    }
  };

  // Fonction pour vérifier si les paramètres de recherche ont changé
  const checkParamsChanged = (currentParams) => {
    if (!profilesCache.params) return true;
    
    const { distance: cachedDistance, genderPreference: cachedGender, ageSearch: cachedAge } = profilesCache.params;
    return cachedDistance !== distance || 
           cachedGender !== genderSearch || 
           JSON.stringify(cachedAge) !== JSON.stringify(ageSearch);
  };

  // Fonction pour vérifier si le cache est encore valide (moins d'une heure d'anciennete)
  const isCacheValid = () => {
    if (!profilesCache.timestamp) return false;
    const oneHour = 60 * 60 * 1000; // 1 Heure en millisecondes
    return (Date.now() - profilesCache.timestamp) < oneHour;
  };

  // Fonction pour mettre à jour le cache des profils
  const updateProfilesCache = (updatedProfiles) => {
    const currentParams = { distance, genderPreference:genderSearch, ageSearch };
    dispatch(setCachedProfiles({
      profiles: updatedProfiles,
      params: currentParams
    }));
  };

  // Fonction pour mettre à jour les removedProfiles avec une limite de 10
  const updateRemovedProfiles = (newProfile) => {
    const updatedRemovedProfiles = [newProfile, ...removedProfiles].slice(0, 10);
    setRemovedProfiles(updatedRemovedProfiles);
    dispatch(updateCachedRemovedProfiles(updatedRemovedProfiles));
  };

  // Effet pour le chargement initial et le rafraîchissement des profils
  useEffect(() => {
    // Vérifier la localisation si nécessaire (sauf en dev) UNIQUEMENT si pas encore de localisation valide
    if (window.location.hostname !== '192.168.31.222' && window.location.hostname !== 'localhost') {
      if (!hasValidLocation) {
        checkLocation();
      }
    }

    // Only load profiles on initial mount if we have all required data
    if (initialLoadRef.current && userId && hasValidLocation) {
      initialLoadRef.current = false;
      fetchProfiles(true);
      return;
    }

    // Only refresh if explicitly requested through refreshProfiles
    if (refreshProfiles && !initialLoadRef.current && !isInitialLoading) {
      console.log('[Home] Refresh requested - fetching profiles');
      setCurrentIndex(0);
      setProfiles([]);
      clearProfilesCache();
      setRemovedProfiles([]); // Vider l'historique
      fetchProfiles(true);
      // Reset refreshProfiles after fetching
      dispatch(setRefreshProfiles(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshProfiles, userId, hasValidLocation]); // Ajout de hasValidLocation dans les dépendances

  useEffect(() => {
    if (profiles.length === 0 || isFetching || initialLoadRef.current) return;

    // Charger plus de profils quand il n'en reste que 2
    if (canLoadMore && profiles.length <= 2) {
      clearProfilesCache();
      fetchProfiles();
    }
  // Ne doit se déclencher que sur le changement de nombre de profils, pas sur canLoadMore/isFetching
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles.length]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
   
    try {
      fetchProfiles()
    } catch (error) {
      console.error('Error refreshing profiles:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleStarClick = () => {
    if (invitations <= 0) {
      setShowInvitationsOffcanvas(true);
      return;
    }
    
    const currentIdx = currentIndex;
    setPendingCard({ index: currentIdx, profile: profiles[currentIdx] });
    setSelectedProfile(profiles[currentIdx]);
    setShowInviteOffcanvas(true);
  };

  const handleCloseInvite = () => {
    setShowInviteOffcanvas(false);
    setPendingCard(null); // Réinitialiser la carte en attente si l'utilisateur ferme sans choisir
    setSelectedProfile(null);
  };

  const handleInviteSubmit = async (inviteType) => {
    setIsInviteLoading(true);
    try {
   
      
      const swipeData = {
        uid: authState.uid,
        other_uid: selectedProfile.uid,
        action: 'superlike',
        invite: inviteType,
        createdAt: moment().utc().unix(),
        status: 'Active'
      };
      
      const data = await cardService.createSwipe(swipeData);
      
      dispatch(decreaseInvitations());
      
      if (data && data.customId) {
        // Créer une conversation dans Firestore
        const db = getFirestore();
        
        // Récupérer les deux UIDs depuis response.data.members
        const [uid1, uid2] = data.members;
        
        // Vérifier si une conversation existe déjà avec ces membres
        const conversationsRef = collection(db, 'Conversations');
        const q = query(
          conversationsRef,
          where('members', 'array-contains-any', [uid1, uid2])
        );
        
        const querySnapshot = await getDocs(q);
        const existingConversation = querySnapshot.docs.find(doc => {
          const data = doc.data();
          return data.members.includes(uid1) && data.members.includes(uid2);
        });

        if (!existingConversation) {
          const conversationData = {
            matches_id: data.customId,
            from: 'swipe',
            members: data.members,
            [authState.uid]: moment().utc().unix(),
            latestMessage: null,
            invite: data.members,
            status: 'Active',
            updateAt: moment().utc().unix()
          };

          // Créer le document dans la collection Conversations
          await setDoc(doc(db, 'Conversations', data.customId), conversationData);
        } else {
        }
        setMatchedProfile(selectedProfile);
        setMatchId(data.customId);
        setShowMatchScreen(true);
      }
      
      // Supprimer le profil après l'envoi réussi de l'invitation
      if (pendingCard) {
        const idx = pendingCard.index;
        const newProfiles = profiles.filter((_, index) => index !== idx);
        setProfiles(newProfiles);
        updateCurrentIndex(newProfiles.length - 1);
        updateProfilesCache(newProfiles);
        setPendingCard(null); // Réinitialiser la carte en attente
        setSelectedProfile(null); // Réinitialiser le profil en attente
        setShowInviteOffcanvas(false)
      }
    } catch (error) {
      console.error('Error recording swipe with invite:', error);
    } finally {
      setIsInviteLoading(false);
    }
  };
  
  const handleSendMessage = async (message) => {
    setIsMessageSending(true);
    try {
    
      
      // Create conversation document in Firestore
      const conversationId = authState.uid + '_' + currentProfile.uid;
      const conversationData = {
        matches_id: conversationId,
        from: 'directmsg',
        members: [authState.uid, currentProfile.uid],
        [authState.uid]: moment().utc().unix(),
        latestMessage: {
          createdAt: moment().utc().unix(),
          messageType: 'text',
          text: message,
          user: {
            _id: authState.uid
          },
          _id: 1
        },
        invite: '',
        status: 'Active',
        updateAt: moment().utc().unix()
      };

      // Get Firestore instance
      const db = getFirestore();
      
      // Create conversation document
      const conversationRef = doc(db, 'Conversations', conversationId);
      await setDoc(conversationRef, conversationData);

      // Create message in Messages subcollection
      const messageData = {
        createdAt: moment().utc().unix(),
        messageType: 'text',
        text: message,
        user: {
          _id: authState.uid
        },
        _id: 1
      };
      const messagesRef = collection(conversationRef, 'Messages');
      await addDoc(messagesRef, messageData);

      // Send message through API
      // await axios.post('/message/send', {
      //   recipientId: currentProfile.uid,
      //   message: message
      // }, {
      //   headers: {
      //     'Authorization': `Bearer ${idToken}`
      //   }
      // });
      handleSwipe('right')
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
    } finally {
      setIsMessageSending(false);
    }
  };

  const swiped = async (direction, nameToDelete, index) => {

    setLastDirection(direction);
    if ((direction === 'right' || direction === 'left') && !canSwipe()) {
      setShowSubscriptionModal(true);
      setCurrentPlan('plus');
      setTitle(t('home.subscription.dailyLimitTitle'));
      // navigate('/subscription');
      return;
    }
 
    if (direction === 'up' && invitations <= 0) {
      setShowInvitationsOffcanvas(true);

      return;
    }


    setLastDirection(direction);
    
    // Mettre à jour le profil courant
    if (index < profiles.length - 1) {
      setCurrentProfile(profiles[index + 1]);
    } else {
      setCurrentProfile(null);
    }
    
    if (direction === 'up') {
      return;
    }
    
    try {
      // Préparer les données pour la requête
      const swipedProfile = profiles[index];
      const swipeData = {
        uid: authState.uid,
        other_uid: swipedProfile.uid,
        action: direction === 'right' ? 'like' : 'dislike',
        invite: null,
        createdAt: moment().utc().unix(),
        status: 'Active'
      };
      
      // Envoyer la requête
      const data = await cardService.createSwipe(swipeData);
      // Mettre à jour le cache avec les profils restants
      // Mettre à jour les stats dans Redux
      dispatch(updateSwipeStats({
        dailySwipes: swipeStats.dailySwipes + 1,
        lastSwipeDate: new Date().toISOString().split('T')[0]
      }));
      
      // Sauvegarder dans la base de données
      try {
        await cardService.updateSwipeStats(userId, {
          dailySwipes: swipeStats.dailySwipes + 1,
          lastSwipeDate: new Date().toISOString().split('T')[0]
        });
      } catch (error) {
        console.error('Error updating swipe stats:', error);
      }

      // Si c'est un match
      if (data && data.customId) {
        // Créer une conversation dans Firestore
        const db = getFirestore();
        
        // Récupérer les deux UIDs depuis response.data.members
        const [uid1, uid2] = data.members;
        
        // Vérifier si une conversation existe déjà avec ces membres
        const conversationsRef = collection(db, 'Conversations');
        const q = query(
          conversationsRef,
          where('members', 'array-contains', uid1)
        );
        
        const querySnapshot = await getDocs(q);
        const existingConversation = querySnapshot.docs.find(doc => {
          const data = doc.data();
          return data.members.includes(uid1) && data.members.includes(uid2);
        });

        if (!existingConversation) {
          const conversationData = {
            matches_id: data.customId,
            from: 'swipe',
            members: data.members,
            [authState.uid]: moment().utc().unix(),
            latestMessage: null,
            invite: data.invite,
            status: 'Active',
            updateAt: moment().utc().unix()
          };

          // Créer le document dans la collection Conversations
          await setDoc(doc(db, 'Conversations', data.customId), conversationData);
        } else {
        }

        setMatchedProfile(swipedProfile);
        setMatchId(data.customId);
        setShowMatchScreen(true);
      }else{
        const removedProfile = swipedProfile;
        const newRemovedProfiles = [removedProfile, ...removedProfiles];
        setRemovedProfiles(newRemovedProfiles);
        updateRemovedProfiles(swipedProfile);
      }
      
      console.log('Swipe recorded successfully');
    } catch (error) {
      console.error('Error recording swipe:', error);
    }
  };

  const updateCurrentIndex = (val) => {
    setCurrentIndex(val);
    currentIndexRef.current = val;
  };

  const handleMessageClick = () => {
    if (!hasPlatinumAccess(authState)) {
      setShowSubscriptionModal(true);
      setCurrentPlan('platinum');
      setTitle(t('home.subscription.upgradeToMessage'));
      return;
    }
  
    setIsMessageModalVisible(true);
  };
  const handleGoBackClick = () => {
    if (!hasActiveSubscription(authState)) {
      setShowSubscriptionModal(true);
      setCurrentPlan('plus');
      setTitle(t('home.subscription.upgradeToGoBack'));
      return;
    }
    
    if (removedProfiles.length === 0) {
      return; // Ne rien faire s'il n'y a pas de profils précédents
    }

    goBack();
  };

  // Afficher un loader si initialisation ou vérification de localisation en cours
  if (isInitialLoading || isChecking || !hasValidLocation) {
    return (
      <Spin size="large" fullscreen tip={
        !hasValidLocation ? t('location.required.title') || 'Localisation requise' : t('home.loading.profiles')
      } />
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="page-content">
          <div className="container">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      {viewProfile && (
        <ProfileDetail 
          user={viewProfile}
          userLocation={[longitude, latitude]}
          onClose={() => setViewProfile(null)}
        />
      )}
      {/* Preloader */}
      <div id="preloader">
        <div className="loader">
          <div className="load-circle"><div></div><div></div></div>
        </div>
      </div>

      {/* Header */}
      <header className="header header-fixed border-0 style-2 bg-white">
        <div className="container">
          <div className="header-content">
            <div className="left-content header-logo logo-lg">
              <Link to="/">
                <img src={require('../../assets/images/fulllogo_transparent_nobuffer.png')} alt="" />
              </Link>
            </div>
            <div className="mid-content">
            </div>
            <div className="right-content d-flex gap-2">
              <button className="filter-icon" data-bs-toggle="offcanvas" data-bs-target="#settingCanvas">
                <i className="flaticon flaticon-settings-sliders"></i>
              </button>

            </div>
          </div>
        </div>
      </header>

      {/* GPS Banner si localisation IP temporaire active */}
      {showGpsBanner && isTemporaryLocation && <GpsBanner />}

      {/* Page Content */}
      <div className="page-content fixed bg-white space-top p-b100">
        <div className="container fixed-full-area">
          <div className="dzSwipe_card-cont dz-gallery-slider">
            {profiles.length > 0 ? (
              profiles.map((profile, index) => (
                <TinderCard
                  ref={childRefs[index]}
                  key={`${index}tinder-card-${profile.uid}`}
                  onSwipe={(dir) => swiped(dir, profile.name, index)}
                  onCardLeftScreen={(dir) => outOfFrame(profile.name, index, dir)}
                  className="swiper-slide"
                preventSwipe={['up','down']} // Permettre le swipe vers le haut
                  swipeRequirementType="position"
                  swipeThreshold={100}
                  onSwipeRequirementFulfilled={(dir) => {
                    updateSwipingClass(profile.uid, dir);
                  }}
                  onSwipeRequirementUnfulfilled={() => {
                    updateSwipingClass(profile.uid, null);
                  }}
                >
                  <div className="dzSwipe_card" data-profile-id={profile.uid}>
                    <div className="image-progress-bar">
                      {profile.photos.map((photo, imgIndex) => (
                        <div
                          key={`progress-${profile.uid}-${imgIndex}`}
                          className={`progress-segment ${imgIndex === (currentImageIndexes[profile.uid] || 0) ? 'active' : ''}`}
                        />
                      ))}
                    </div>
                    <div className={`swipe-indicator like ${(showSwipeIndicators) ? 'visible' : 'hidden'}`}>{t('home.swipe.like')}</div>
                    <div className={`swipe-indicator nope ${(showSwipeIndicators) ? 'visible' : 'hidden'}`}>{t('home.swipe.nope')}</div>
                    <div className="dz-media">
                      <img src={profile.photos[currentImageIndexes[profile.uid] || 0].photoUrl} alt={profile.name} />
                      <button 
                        type="button"
                        className="image-navigation left"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleImageNavigation(profile.uid, 'prev');
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleImageNavigation(profile.uid, 'prev');
                        }}
                      />
                      <button 
                        type="button"
                        className="image-navigation right"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleImageNavigation(profile.uid, 'next');
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleImageNavigation(profile.uid, 'next');
                        }}
                      />
                    </div>
                    <div className="dz-content">
                      <div className="left-content">
                        <h4 className="title">
                          <Link to="/profile-detail">{profile.name}, {calculateAge(profile.doB)}</Link>
                        </h4>
              
                        <p className="mb-0">
                          <i className="icon feather icon-map-pin"></i> {calculateDistance([longitude, latitude], profile.location)} km 
                        </p>
                      </div>
                      <button 
                        className="profile-detail-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setViewProfile(profile);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setViewProfile(profile);
                        }}
                      >
                        <i className="fa fa-info-circle"></i>
                      </button>
                    </div>
                  </div>
                </TinderCard>
              ))
            ) : (
              <div className="no-profiles-container text-center">
                <div className="no-profiles-content">
                  <img 
                    src={authState.photos[0]?.photoUrl || require('../../assets/images/user/pic1.jpg')} 
                    alt={t('home.noProfiles.title')}
                    className="mb-4"
                    style={{ width: '120px' }}
                  />
                  <h4>{t('home.noProfiles.title')}</h4>
                  <p>{t('home.noProfiles.description')}</p>
                  <button 
                    className="btn btn-primary"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? (
                      <Spin size="small" />
                    ) : (
                      <><i className="fas fa-sync-alt mr-2"></i> {t('home.noProfiles.refresh')}</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Actions buttons */}
          {profiles.length > 0 && (
            <div className="swipe-buttons">
              <button 
                className={`btn-action btn-undo `}
                onClick={handleGoBackClick}
              >
                <i className="fa-solid fa-rotate-left"></i>
              </button>

              <button 
                className={`btn-action btn-nope `}
                onClick={() => handleSwipe('left')}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>

              <button 
                className={`btn-action btn-action btn-star`}
                onClick={handleStarClick}
              >
                <i className="fa-solid fa-star"></i>
                {invitations > 0 && <span className="invite-count">{invitations}</span>}
              </button>

              <button 
                className={`btn-action btn-like `}
                onClick={() => handleSwipe('right')}
              >
                <i className="fa-solid fa-heart"></i>
              </button>

              <button 
                className={`btn-action btn-message `}
                onClick={handleMessageClick}
              >
                <i className="fa-solid fa-comment"></i>
              </button>
            </div>
          )}
        </div>
      </div>
      <SettingsOffcanvas />
      <InvitationsOffcanvas
        show={showInvitationsOffcanvas}
        onClose={() => setShowInvitationsOffcanvas(false)}
      />
      <InviteOffcanvas
        show={showInviteOffcanvas}
        onHide={handleCloseInvite}
        onClose={handleCloseInvite}
        onSubmit={handleInviteSubmit}
        profile={selectedProfile}
        isLoading={isInviteLoading}
      />
      <MessageModal 
        isVisible={isMessageModalVisible}
        onClose={() => setIsMessageModalVisible(false)}
        onSend={handleSendMessage}
        profile={currentProfile}
      />
      
      {isMessageSending && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <Spin size="large" fullscreen tip={t('home.loading.sendingMessage')} />
        </div>
      )}
      
      {showMatchScreen && matchedProfile && (
        <MatchScreen
          user1={authState}
          user2={matchedProfile}
          matchId={matchId}
          onClose={() => setShowMatchScreen(false)}
        />
      )}
                    <SubscriptionOffcanvas
                    show={showSubscriptionModal}
                    title={title}
                    onClose={() => setShowSubscriptionModal(false)}
                    planType={currentPlan}
                />
      {/* <SubscriptionModal
        show={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        currentPlan={currentPlan}
      /> */}
    </div>
  );
};

export default Home;
